/**
 * useUsers Hook
 * Provides access to users data for autocomplete functionality
 */

import { useAtom } from "jotai";
import { useMemo, useCallback } from "react";
import { usersQueryAtom, superusersQueryAtom } from "@/store/adminDataStore";
import { employeesQueryAtom } from "@/store/referenceDataStore";
import type { IUser } from "@/services";
import {
  applyNameFallback,
  fallbackFromEmployee,
  fallbackFromSuperuser,
  normalizeEmail,
  upsertNameFallback,
} from "./dataQuality";
import type { NameFallback } from "./dataQuality";

export interface UseUsersReturn {
  /** All users from API */
  users: IUser[];
  /** Users sorted alphabetically by name */
  sortedUsers: IUser[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Find user by ID */
  findById: (id: number) => IUser | undefined;
  /** Find user by email */
  findByEmail: (email: string) => IUser | undefined;
  /** Search users by name or email */
  searchUsers: (query: string) => IUser[];
}

export function useUsers(): UseUsersReturn {
  const [query] = useAtom(usersQueryAtom);
  const [superusersQuery] = useAtom(superusersQueryAtom);
  const [employeesQuery] = useAtom(employeesQueryAtom);

  // Stable reference to prevent unnecessary recalculations
  const rawUsers = useMemo(() => query.data ?? [], [query.data]);
  const fallbackMap = useMemo(() => {
    const map = new Map<string, NameFallback>();
    employeesQuery.data?.forEach((employee) => {
      upsertNameFallback(map, employee.email, fallbackFromEmployee(employee));
    });
    superusersQuery.data?.forEach((superuser) => {
      upsertNameFallback(
        map,
        superuser.email,
        fallbackFromSuperuser(superuser),
      );
    });
    return map;
  }, [employeesQuery.data, superusersQuery.data]);

  const users = useMemo(() => {
    if (!rawUsers.length) return [];
    return rawUsers.map((user) => {
      const emailKey = normalizeEmail(user.email);
      if (!emailKey) return user;
      const fallback = fallbackMap.get(emailKey);
      if (!fallback) return user;
      return applyNameFallback(user, fallback);
    });
  }, [rawUsers, fallbackMap]);

  // Sort alphabetically by name
  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => a.name.localeCompare(b.name)),
    [users],
  );

  // Find by ID
  const findById = useCallback(
    (id: number) => users.find((u) => u.id === id),
    [users],
  );

  // Find by email
  const findByEmail = useCallback(
    (email: string) =>
      users.find((u) => u.email.toLowerCase() === email.toLowerCase()),
    [users],
  );

  // Search users by name or email
  const searchUsers = useCallback(
    (searchQuery: string): IUser[] => {
      if (!searchQuery.trim()) return [];
      const lowerQuery = searchQuery.toLowerCase();
      return sortedUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(lowerQuery) ||
          u.email.toLowerCase().includes(lowerQuery),
      );
    },
    [sortedUsers],
  );

  return {
    users,
    sortedUsers,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
    findById,
    findByEmail,
    searchUsers,
  };
}
